import { useRef, useState } from "react";
import { useLocation } from "@tanstack/react-router";

import { NAV_SECTIONS } from "@degaus/docs/nav";

import { docHref, findDocLocation } from "@/landing/docs/docs-nav";
import { SideNavGroup, SideNavLink } from "@/landing/system/sidenav";

function buildOpenSections(pathname: string): Record<string, boolean> {
  const current = findDocLocation(pathname)?.section.title;
  return Object.fromEntries(
    NAV_SECTIONS.map((section) => [section.title, section.title === current]),
  );
}

export function DocsSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = useLocation().pathname ?? "";
  const prevPathnameRef = useRef(pathname);
  const [openSections, setOpenSections] = useState(() =>
    buildOpenSections(pathname),
  );

  if (prevPathnameRef.current !== pathname) {
    prevPathnameRef.current = pathname;
    setOpenSections(buildOpenSections(pathname));
  }

  const toggleSection = (title: string) => {
    setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <nav aria-label="Docs" className="flex flex-col gap-0.5">
      {NAV_SECTIONS.map((section) => (
        <SideNavGroup
          key={section.title}
          title={section.title}
          open={openSections[section.title] ?? false}
          onToggle={() => toggleSection(section.title)}
        >
          {section.items.map((item) => {
            const href = docHref(item);
            return (
              <SideNavLink
                key={item.label}
                to={href}
                active={pathname.replace(/\/+$/, "") === href}
                onClick={onNavigate}
              >
                {item.label}
              </SideNavLink>
            );
          })}
        </SideNavGroup>
      ))}
    </nav>
  );
}
